/**
 * MCP 플레이그라운드 — StreamableHTTP 엔드포인트에 직접 JSON-RPC 요청을 보낸다.
 * 2-tier 전용: `/mcp/:slug/:server` 만 지원.
 *
 * `@pioncorp/knowledge-client` SDK 는 Node 트랜스포트에 의존하므로 브라우저에서는 fetch 로 간단히
 * JSON 응답을 받는다 (`enableJsonResponse` 모드).
 */
export type ToolName =
	| "knowledge_search"
	| "knowledge_traverse"
	| "knowledge_upsert"
	| "knowledge_invalidate_node"
	| "docs_list"
	| "docs_search"
	| "docs_read"
	| "edge_attach_episode"
	| "edge_link_episode"
	| "edge_list_episodes"
	| "edge_semantic_search"
	| "edge_invalidate_episode"
	| "edge_invalidate_link"
	| "edge_invalidate"

/** MCP 툴 호출 — `/mcp/:slug/:server` 로 JSON-RPC POST. */
export async function callMcpTool(args: {
	readonly baseUrl: string
	readonly serviceSlug: string
	readonly serverName: string
	readonly apiKey: string
	readonly tool: ToolName
	readonly input: Record<string, unknown>
}): Promise<unknown> {
	const requestId = Math.floor(Math.random() * 1_000_000)
	const path = `/mcp/${encodeURIComponent(args.serviceSlug)}/${encodeURIComponent(args.serverName)}`
	const res = await fetch(`${args.baseUrl}${path}`, {
		method: "POST",
		headers: {
			authorization: `Bearer ${args.apiKey}`,
			accept: "application/json, text/event-stream",
			"content-type": "application/json"
		},
		body: JSON.stringify({
			jsonrpc: "2.0",
			id: requestId,
			method: "tools/call",
			params: { name: args.tool, arguments: args.input }
		})
	})

	if (!res.ok) {
		const text = await res.text()
		throw new Error(`mcp ${args.tool} ${res.status}: ${text}`)
	}

	const json = (await res.json()) as {
		result?: { content?: ReadonlyArray<{ type: string; text?: string }> }
		error?: { code: number; message: string }
	}
	if (json.error) throw new Error(`${json.error.code}: ${json.error.message}`)
	const text = json.result?.content?.[0]?.text
	if (text === undefined) return json.result
	try {
		return JSON.parse(text)
	} catch {
		return text
	}
}
